import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { EmptyState, PageHeader, StatusBadge } from "./DashboardUI";
import TicketDetailModal from "../modals/TicketDetailModal";
import {
  getAdminTickets,
  getTicketTypes,
} from "../../redux/actions/TicketActions";

const TICKET_STATUS = {
  initiated: "En attente",
  in_progress: "En cours",
  fulfilled: "Terminée",
  failed: "Échouée",
};

// Admin page of a service: requests of all clients, read only
function AdminServiceTickets({ serviceCode, title, lead }) {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.authUser);
  const { ticketTypes, adminTickets } = useSelector((state) => state.tickets);

  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!user?.token) return;

    setLoading(true);
    Promise.all([
      dispatch(getTicketTypes()),
      dispatch(getAdminTickets(user.token, serviceCode)),
    ]).finally(() => setLoading(false));
  }, [user?.token, serviceCode, dispatch]);

  const typeName = (code) =>
    (ticketTypes || []).find((item) => item.code === code)?.name || "Demande";

  const term = search.trim().toLowerCase();
  const tickets = (adminTickets || [])
    .filter(
      (ticket) =>
        (!status || ticket.status === status) &&
        (!term ||
          ticket.reference?.toLowerCase().includes(term) ||
          ticket.company?.businessName?.toLowerCase().includes(term) ||
          typeName(ticket.reclamationCode).toLowerCase().includes(term)),
    )
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const pending = (adminTickets || []).filter(
    (ticket) => ticket.status === "initiated",
  ).length;

  return (
    <>
      <PageHeader
        title={title}
        lead={
          pending
            ? `${pending} demande${pending > 1 ? "s" : ""} en attente de traitement.`
            : lead
        }
      />

      <div className="dash-card">
        <div className="dash-filters">
          <input
            type="search"
            placeholder="Rechercher une référence, une entreprise ou un type"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Rechercher"
          />
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            aria-label="Statut"
          >
            <option value="">Tous les statuts</option>
            {Object.entries(TICKET_STATUS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <p className="dash-loading">Chargement des demandes…</p>
        ) : tickets.length === 0 ? (
          <EmptyState title="Aucune demande">
            Aucune demande ne correspond à ces critères.
          </EmptyState>
        ) : (
          <div className="dash-table-wrap">
            <table className="dash-table is-responsive">
              <thead>
                <tr>
                  <th>Demande</th>
                  <th>Entreprise</th>
                  <th>Référence</th>
                  <th>Date</th>
                  <th>Statut</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {tickets.map((ticket) => (
                  <tr key={ticket.reference}>
                    <td>
                      <span className="dash-cell-title">
                        {typeName(ticket.reclamationCode)}
                      </span>
                    </td>
                    <td data-label="Entreprise">
                      {ticket.company?.businessName || "—"}
                    </td>
                    <td data-label="Référence">#{ticket.reference}</td>
                    <td data-label="Date">
                      {moment(ticket.createdAt).format("DD/MM/YYYY")}
                    </td>
                    <td data-label="Statut">
                      <StatusBadge
                        status={ticket.status}
                        label={TICKET_STATUS[ticket.status]}
                      />
                    </td>
                    <td>
                      <div className="dash-row-actions">
                        <button
                          type="button"
                          className="dash-link-button"
                          onClick={() =>
                            setSelected({
                              ...ticket,
                              type: typeName(ticket.reclamationCode),
                            })
                          }
                        >
                          Voir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <TicketDetailModal
        show={Boolean(selected)}
        onHide={() => setSelected(null)}
        ticket={selected}
      />
    </>
  );
}

export default AdminServiceTickets;
